import {
  HttpAgent,
  type AgentSubscriber,
  type Context,
  type RunAgentInput,
} from "@ag-ui/client";
import { getConfig } from "./config";
import { csrfToken } from "@/auth/csrf";

// AG-UI transport for the Assistant chat turn (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption). Uses the official
// @ag-ui/client HttpAgent, pointed at the BFF /ai/agui passthrough (which injects the Bearer,
// exactly like /ai/stream). Sessions stay server-authoritative: the agent-service reads only the
// LAST user message of `messages[]`, so we set exactly that one message per run instead of
// accumulating client history. The @adr SPARK-0014-design-primitives-single-source@1 person-load endpoints still use the legacy SSE parser
// in agent-stream.ts.

// Cookie-session auth + per-request CSRF header (read at request time — the token can rotate),
// mirroring the legacy SSE fetch options.
class SparkAgent extends HttpAgent {
  protected override requestInit(input: RunAgentInput): RequestInit {
    const init = super.requestInit(input);
    const csrf = csrfToken();
    return {
      ...init,
      credentials: "include",
      headers: {
        ...(init.headers as Record<string, string>),
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
    };
  }
}

export function createAssistantAgent(): HttpAgent {
  return new SparkAgent({ url: `${getConfig().basePath}ai/agui` });
}

// The drawer's view of a turn — same shape the legacy SSE callbacks fed, so the store wiring
// is unchanged. `onToolCall` fires at TOOL_CALL_END with the fully-parsed args.
export interface AguiTurnHandlers {
  onThreadId: (id: string) => void;
  onDelta: (delta: string) => void;
  onToolCall: (name: string, args: Record<string, unknown>, toolCallId: string) => void;
  // Fires at TOOL_CALL_RESULT with the call's outcome. A completed tool call can still be a
  // failure (`isError` true) — correlate by `toolCallId` to the matching onToolCall to gate
  // success-only side effects.
  onToolResult: (toolCallId: string, result: ToolResultMeta) => void;
  onSuggestions: (items: string[]) => void;
  onError: () => void;
}

export interface ToolResultMeta {
  isError: boolean;
  resultId: string | null;
}

export interface AgentBindingContext {
  matchId: string;
}

// agent-service streams the MCP CallToolResult outcome as the TOOL_CALL_RESULT `content`: a JSON
// string `{"type":"tool_result","source":…,"size":…,"isError":bool,"resultId":…}`. A *completed*
// tool call can still be a failure (e.g. genesis rejected a write) — that surfaces as
// `isError:true`, not a run error. Absent/malformed ⇒ success with no result id, matching results
// that carry no flag.
function parseToolResult(content: string): ToolResultMeta {
  try {
    const parsed = JSON.parse(content) as { isError?: unknown; resultId?: unknown };
    return {
      isError: parsed.isError === true,
      resultId: typeof parsed.resultId === "string" && parsed.resultId ? parsed.resultId : null,
    };
  } catch {
    return { isError: false, resultId: null };
  }
}

// Idle watchdog window for a chat turn (spark#228 C3). A turn can legitimately
// pause between events — a slow tool call leaves a real gap between
// TOOL_CALL_END and TOOL_CALL_RESULT — so this must be generous enough to catch
// a wedge, not clip a slow turn.
export const DEFAULT_TURN_IDLE_MS = 45_000;

// Run one chat turn. Resolves when the run finishes (RUN_FINISHED / RUN_ERROR / abort); rejects on
// transport failure before/while streaming. `threadId` is the agent-service session id (null →
// let the client mint one; the server creates the session under it and echoes it in RUN_STARTED).
export async function runAguiTurn(
  agent: HttpAgent,
  opts: {
    message: string;
    section: string;
    threadId: string | null;
    uiContext: Context[];
    // Tool-binding/gating context (agent-service reads it from `forwardedProps.context`, NOT the
    // top-level AG-UI `context` which only feeds the system prompt). Carries the focused entity id
    // (e.g. `{matchId}`) so agent-service offers that match's write tools; omit when nothing focused.
    bindingContext?: AgentBindingContext;
    abortController: AbortController;
    // Idle window before a stalled turn is aborted. Defaults to DEFAULT_TURN_IDLE_MS.
    idleMs?: number;
  },
  handlers: AguiTurnHandlers,
): Promise<void> {
  // Always assign: HttpAgent retains its threadId across runs, so on a reused agent instance a
  // null (new chat) must mint a FRESH id — the server load-or-creates the session under whatever
  // threadId rides up, so a stale one would silently continue the previous conversation.
  agent.threadId = opts.threadId ?? crypto.randomUUID();
  // Server-authoritative history: only the latest user message rides up (see module comment).
  agent.setMessages([{ id: crypto.randomUUID(), role: "user", content: opts.message }]);

  // Idle watchdog: after idleMs of complete silence, surface a client error and
  // abort the transport so a mid-stream stall doesn't hang forever. We call
  // handlers.onError() directly rather than relying on the abort to surface it:
  // @ag-ui/client's HttpAgent SWALLOWS an AbortError (it neither rejects runAgent
  // nor emits onRunErrorEvent), so a bare abort would let the drawer treat the
  // stall as a normal end. Reset the timer on EVERY event via onEvent.
  const idleMs = opts.idleMs ?? DEFAULT_TURN_IDLE_MS;
  let idle: ReturnType<typeof setTimeout> | undefined;
  let stalled = false;
  const clearIdle = () => {
    if (idle) clearTimeout(idle);
    idle = undefined;
  };
  const armIdle = () => {
    if (stalled) return;
    clearIdle();
    idle = setTimeout(() => {
      stalled = true;
      handlers.onError();
      opts.abortController.abort();
    }, idleMs);
  };

  const subscriber: AgentSubscriber = {
    onEvent: () => armIdle(),
    onRunStartedEvent: ({ event }) => handlers.onThreadId(event.threadId),
    onTextMessageContentEvent: ({ event }) => handlers.onDelta(event.delta),
    onToolCallEndEvent: ({ event, toolCallName, toolCallArgs }) =>
      handlers.onToolCall(toolCallName, toolCallArgs, event.toolCallId),
    onToolCallResultEvent: ({ event }) =>
      handlers.onToolResult(event.toolCallId, parseToolResult(event.content)),
    onCustomEvent: ({ event }) => {
      if (event.name === "suggestions" && Array.isArray(event.value)) {
        handlers.onSuggestions(event.value.filter((v): v is string => typeof v === "string"));
      }
    },
    // A genuine RUN_ERROR from the stream still routes here; the stall path
    // already called onError, so don't double-report.
    onRunErrorEvent: () => {
      if (!stalled) handlers.onError();
    },
  };

  armIdle();
  try {
    await agent.runAgent(
      {
        context: opts.uiContext,
        forwardedProps: {
          section: opts.section,
          ...(opts.bindingContext ? { context: opts.bindingContext } : {}),
        },
        abortController: opts.abortController,
      },
      subscriber,
    );
  } finally {
    clearIdle();
  }
}
